/**
 * @page /portal/vertraege
 * @description Meine Dienstverträge — konsolidierte Vertragsliste aus mitarbeiter_vertraege.
 * @api GET /api/portal/vertraege
 * @api GET /api/portal/vertraege/:id/download
 * @api GET /api/portal/vertrag-dokumente/:id/download (Fallback)
 * @roles mitarbeiter, admin, manager
 * @i18n portalContracts
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, KpiCard, PageHeader } from '../components/ui';
import { ClipboardList, CheckCircle, FileText, Inbox } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function fmtDate(d) {
  if (!d) return null;
  const dt = typeof d === 'string' ? d.slice(0, 10) : d;
  const [y, m, day] = dt.split('-');
  return `${day}.${m}.${y}`;
}

const statusColors = {
  'Laufend': 'text-emerald-700 bg-emerald-100',
  'Abgeschlossen': 'text-[var(--text-tertiary,#6B7280)] bg-[var(--bg-inset,#F8F9FC)]',
  'Entwurf': 'text-amber-700 bg-amber-100',
  'Storniert': 'text-red-700 bg-red-100',
  'Bevorstehend': 'text-blue-700 bg-blue-100',
  'Probezeit': 'text-blue-700 bg-blue-100',
  'Neu': 'text-cyan-700 bg-cyan-100',
  'unterschrieben': 'text-emerald-700 bg-emerald-100',
};

const typLabelKeys = {
  vollzeit: 'portalContracts.typeFulltime',
  teilzeit: 'portalContracts.typeParttime',
  geringfuegig: 'portalContracts.typeMarginal',
  praktikum: 'portalContracts.typeInternship',
  sonstig: 'portalContracts.typeOther',
};

export default function PortalVertraege() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [vertraege, setVertraege] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdminOrManager = ['admin', 'administrator', 'manager', 'ops_manager', 'teamleiter'].includes(user?.rolle);

  useEffect(() => {
    fetch(`${API}/api/portal/vertraege`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setVertraege(Array.isArray(data) ? data : []))
      .catch(() => setVertraege([]))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = (v) => {
    const isDv = String(v.id).startsWith('dv-');
    const dvId = isDv ? String(v.id).replace('dv-', '') : null;
    const url = isDv
      ? `${API}/api/portal/digitale-vertraege/${dvId}/download`
      : `${API}/api/portal/vertraege/${v.id}/download`;
    window.open(url, '_blank');
  };

  const aktiv = vertraege.find(v => v.ist_aktiv || v.status === 'Laufend');
  const totalDocs = vertraege.filter(v => v.pdf_pfad).length;

  return (
    <div className="p-page">
      <PageHeader title={t('portalContracts.title')} subtitle={t('portalContracts.subtitle')} />

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-3 gap-3 mb-section">
            <KpiCard label={t('portalContracts.contracts')} value={vertraege.length} icon={ClipboardList} color="bg-primary-500/10 text-primary-500" />
            <KpiCard label={t('common.status')} value={aktiv ? t('portalContracts.active') : '–'} icon={CheckCircle} color="bg-emerald-500/10 text-emerald-500" />
            <KpiCard label={t('portalContracts.documents')} value={totalDocs} icon={FileText} color="bg-blue-500/10 text-blue-500" />
          </div>

          {/* Vertragsliste */}
          {vertraege.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center mb-4">
                <Inbox size={28} className="text-[var(--text-muted)]" />
              </div>
              <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">
                {t('portalContracts.noData')}
              </h3>
              <p className="text-sm text-[var(--text-tertiary)] max-w-sm">
                {t('portalContracts.noDataDesc')}
              </p>
            </div>
          ) : (
            <div className="bg-surface-card border border-line rounded-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="text-left px-4 py-3 text-[11px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('portalContracts.description')}</th>
                      <th className="text-left px-4 py-3 text-[11px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('portalContracts.period')}</th>
                      <th className="text-left px-4 py-3 text-[11px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('portalContracts.workSchedule')}</th>
                      <th className="text-left px-4 py-3 text-[11px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('common.status')}</th>
                      {isAdminOrManager && (
                        <th className="text-right px-4 py-3 text-[11px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('portalContracts.salary')}</th>
                      )}
                      <th className="text-center px-4 py-3 text-[11px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vertraege.map(v => {
                      const isActive = v.ist_aktiv || v.status === 'Laufend';
                      const hasPdf = !!v.pdf_pfad;
                      return (
                        <tr key={v.id} className={`border-b border-line last:border-0 ${isActive ? 'bg-emerald-500/5' : 'hover:bg-surface-inset/50'} transition-colors`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
                              <span className={`font-medium ${isActive ? 'text-content' : 'text-[var(--text-secondary)]'}`}>
                                {v.bezeichnung || '–'}
                              </span>
                            </div>
                            {v.vertrag_typ && (
                              <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{typLabelKeys[v.vertrag_typ] ? t(typLabelKeys[v.vertrag_typ]) : v.vertrag_typ}</div>
                            )}
                            {(v.stelle || v.position) && <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{v.position || v.stelle}</div>}
                          </td>
                          <td className="px-4 py-3 text-[var(--text-secondary)]">
                            <div>{fmtDate(v.start_datum) || '–'}</div>
                            <div className="text-[10px] text-[var(--text-tertiary)]">
                              {v.end_datum ? `bis ${fmtDate(v.end_datum)}` : t('portalContracts.unlimited')}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[var(--text-secondary)]">
                            <div>{v.arbeitsplan || '–'}</div>
                            {v.stunden_pro_woche && (
                              <div className="text-[10px] text-[var(--text-tertiary)]">{v.stunden_pro_woche} {t('portalContracts.hoursPerWeek')}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColors[v.status] || 'text-[var(--text-tertiary,#6B7280)] bg-[var(--bg-inset,#F8F9FC)]'}`}>
                              {v.status || '–'}
                            </span>
                          </td>
                          {isAdminOrManager && (
                            <td className="px-4 py-3 text-right font-mono text-[var(--text-primary)]">
                              {(v.brutto_monatlich || v.lohn) != null ? `€ ${Number(v.brutto_monatlich || v.lohn).toLocaleString('de-DE', { minimumFractionDigits: 2 })}` : '–'}
                            </td>
                          )}
                          <td className="px-4 py-3 text-center">
                            {hasPdf ? (
                              <button onClick={() => handleDownload(v)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-600/10 hover:bg-primary-600/20 text-primary-400 rounded text-[10px] font-medium transition-colors">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                PDF
                              </button>
                            ) : (
                              <span className="text-xs text-[var(--text-muted)]">–</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
